import { t } from "../i18n";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import AccordionSection from "./AccordionSection.jsx";
import DesignCard from "./DesignCard.jsx";
import TextureGrid from "./TextureGrid.jsx";
import { kitchenLayouts } from "../data/kitchenData.js";
import { cabinetTextures, countertopTextures } from "../data/textureData.js";
import {
  designPricing,
  cabinetTextureGroups,
  countertopTiers,
} from "../data/pricingData.js";

function DisabledTooltip({ children, className }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  function handleMouseEnter() {
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    setShow(true);
  }

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
      className={className}
    >
      {children}
      {show &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top - 8,
              left: pos.left,
              transform: "translate(-50%, -100%)",
            }}
            className="px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap pointer-events-none z-9999"
          >{t("No designs available")}</div>,
          document.body,
        )}
    </div>
  );
}

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  function handleMouseEnter() {
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    setShow(true);
  }

  return (
    <>
      <button
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        className="w-3.5 h-3.5 rounded-full border border-gray-400 text-gray-400 text-[8px] font-bold flex items-center justify-center hover:border-gray-600 hover:text-gray-600 transition-colors leading-none"
      >
        i
      </button>
      {show &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top - 8,
              left: pos.left,
              transform: "translate(-50%, -100%)",
            }}
            className="px-2.5 py-1.5 bg-gray-800 text-white text-[10px] rounded w-52 leading-snug pointer-events-none z-9999"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

function ShapeCard({ shape, available, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  const src =
    hovered && shape.thumbnailHover ? shape.thumbnailHover : shape.thumbnail;

  return (
    <button
      onClick={() => available && onClick()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={!available}
      className={`w-full aspect-square border overflow-hidden transition-opacity ${
        !available
          ? "border-gray-200 bg-gray-50 opacity-30 cursor-default"
          : selected
            ? "border-gray-900 bg-gray-100"
            : "bg-gray-50 border-transparent hover:border-gray-300"
      }`}
    >
      {src && (
        <img
          src={src}
          alt=""
          className="w-full h-full object-contain"
          onError={(e) => {
            e.currentTarget.hidden = true;
          }}
        />
      )}
    </button>
  );
}

const SHAPES = [
  {
    id: "l-shape",
    label: t("LSHAPE"),
    thumbnail: "/images/kitchen-shapes/L Shape- Primary.png",
    thumbnailHover: "/images/kitchen-shapes/L Shape- Secondary.png",
  },
  {
    id: "galley",
    label: t("GALLEY"),
    thumbnail: "/images/kitchen-shapes/Galley Primary.png",
    thumbnailHover: "/images/kitchen-shapes/Galley- Secondary.png",
  },
  {
    id: "u-shape",
    label: t("USHAPE"),
    thumbnail: "/images/kitchen-shapes/U Shape- Primary.png",
    thumbnailHover: "/images/kitchen-shapes/U Shape- Secondary.png",
  },
  {
    id: "1-wall",
    label: t("WALL"),
    thumbnail: "/images/kitchen-shapes/Wall- Primary.png",
    thumbnailHover: "/images/kitchen-shapes/Wall- Secondary.png",
  },
];

const VMKC_SETTINGS = window.VMKitchenConfigurator || {};
const USE_DYNAMIC_DATA =
  VMKC_SETTINGS.useDynamicData === true || VMKC_SETTINGS.dataSource === "dynamic";

function colorPlaceholder(hex = "#e5e7eb") {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="${hex || "#e5e7eb"}"/></svg>`,
  )}`;
}

function termById(terms = [], id) {
  const numericId = Number(id);
  return terms.find((term) => Number(term.id) === numericId) ?? null;
}

/*
 * The key that pairs a design's two handle variants.
 *
 * The original app keeps one design carrying two model files, so toggling the
 * handle never loses the design. Here each variant is its own product, and
 * their SKUs differ only in the trailing handle code - LS3C3A00GL (integrated)
 * and LS3C3A00TB (bar) are the same kitchen. Stripping that code gives a stem
 * that matches the pair.
 */
function designStem(item) {
  return normalizeCode(item?.product?.article_number).replace(/(GL|TB)$/, "");
}

function handleKind(term) {
  const value = `${term?.slug ?? ""} ${term?.name ?? ""}`.toLowerCase();
  return value.includes("gola") ||
    value.includes("integrated") ||
    value.includes("without") ||
    value.includes("no ")
    ? "none"
    : "with";
}

function colorToTexture(color) {
  const textureUrl = color.texture?.url || color.thumbnail || "";
  const swatch = color.thumbnail || textureUrl || colorPlaceholder();

  return {
    id: String(color.id),
    colorId: Number(color.id),
    label: color.name,
    path: textureUrl,
    thumbnail: swatch,
    renderValue: textureUrl || null,
    grade: color.grade,
  };
}

function colorsFor(config, component) {
  return (config?.colors?.[component] ?? []).map(colorToTexture);
}

function firstTexture(textures) {
  return textures[0] ?? null;
}

/*
 * Opening colours, per the client's comment sheet: wood components start on
 * Alpine White and the solid surface countertop on Quasar White. Matched on
 * name because the colour IDs are assigned by WordPress and differ per install.
 */
const DEFAULT_WOOD_COLOR = "alpine white";
const DEFAULT_SURFACE_COLOR = "quasar white";

function textureByName(textures, name) {
  const wanted = String(name).toLowerCase();

  return (
    textures.find((texture) => String(texture.label ?? "").toLowerCase().includes(wanted)) ?? null
  );
}

/**
 * The texture a component should show once the design changes.
 *
 * Two problems came from picking textures[0] here. The opening colour was
 * whatever the admin happened to return first rather than the one the client
 * asked for, and - because this runs on every design change - a colour the
 * customer had chosen was thrown away the moment they picked a different
 * design. So keep the current colour whenever the new design still offers it,
 * and only fall back to the named default when it does not.
 *
 * @param {Array}  textures    Colours available for this component.
 * @param {string} defaultName Colour name to open on.
 * @param {string} currentId   Colour currently selected, if any.
 */
function preferredTexture(textures, defaultName, currentId) {
  if (!textures.length) return null;

  const kept = currentId
    ? textures.find((texture) => texture.id === String(currentId))
    : null;

  return kept ?? textureByName(textures, defaultName) ?? textures[0];
}

function selectedColorId(value) {
  return value ? Number(value) : 0;
}

function dynamicColorById(config, colorId) {
  const numericId = Number(colorId);
  if (!numericId) return null;

  return Object.values(config?.colors ?? {})
    .flat()
    .find((color) => Number(color.id) === numericId) ?? null;
}

function dynamicColorGrade(config, colorId) {
  const grade = dynamicColorById(config, colorId)?.grade || "g1";
  return grade === "none" ? "g1" : grade;
}

function dynamicMatrixPrice(config, group, grade) {
  return Number(config?.price_matrix?.[group]?.[grade || "g1"] || 0);
}

function calculateDynamicPrice(config, cartConfig) {
  if (!config || !cartConfig) return 0;

  const baseGrade = dynamicColorGrade(config, cartConfig.base_color_id);
  const wallGrade = dynamicColorGrade(config, cartConfig.wall_color_id);
  const tallGrade = dynamicColorGrade(config, cartConfig.tall_color_id);
  const countertopGrade = dynamicColorGrade(config, cartConfig.countertop_color_id);

  // Mirror of VM_Kitchen_Configurator::calculate_price_from_ids(). Each component's
  // G1/G2/G3 cell is a finished amount picked by that component's colour grade;
  // Base Price is the article's flat skirting + handle amount. Keep the two in step.
  const subtotal =
    dynamicMatrixPrice(config, "base_price", "g1") +
    dynamicMatrixPrice(config, "base_unit", baseGrade) +
    dynamicMatrixPrice(config, "wall_unit", wallGrade) +
    dynamicMatrixPrice(config, "tall_unit", tallGrade) +
    dynamicMatrixPrice(config, "solid_surface", countertopGrade);

  return subtotal * Number(cartConfig.quantity || 1);
}

const STATIC_DESIGNS = kitchenLayouts.flatMap((layout) =>
  layout.designs.map((design) => ({ ...design, shapeId: layout.id })),
);

function normalizeCode(value) {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function dynamicSearchText(config, design) {
  return [
    config?.product?.article_number,
    config?.product?.name,
    config?.product?.slug,
    design?.slug,
    design?.name,
  ]
    .map(normalizeCode)
    .filter(Boolean)
    .join(" ");
}

function matchingStaticDesign(config, design) {
  const haystack = dynamicSearchText(config, design);
  if (!haystack) return null;

  return (
    STATIC_DESIGNS.find((staticDesign) => haystack.includes(staticDesign.id)) ??
    STATIC_DESIGNS.find((staticDesign) =>
      haystack.includes(staticDesign.id.replace(/TB$/, "GL")),
    ) ??
    null
  );
}

function dynamicModelPath(config, design, handleType) {
  if (config?.model?.url) return config.model.url;
  const fallback = matchingStaticDesign(config, design);
  if (!fallback) return null;
  return handleType === "none" ? fallback.modelGola : fallback.model;
}

function dynamicThumbnailPath(config, design) {
  const fallback = matchingStaticDesign(config, design);
  return (
    config?.product?.thumbnail ||
    config?.media?.inspirations?.[0]?.url ||
    fallback?.thumbnail ||
    ""
  );
}

export default function Configurator(props) {
  return USE_DYNAMIC_DATA ? (
    <DynamicConfigurator {...props} />
  ) : (
    <StaticConfigurator {...props} />
  );
}

function DynamicConfigurator({
  onModelSelect,
  onTextureChange,
  onPriceChange,
  onInfoChange,
  onCartConfigChange,
  onLoadingChange,
}) {
  const [productConfigs, setProductConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kitchenSize, setKitchenSize] = useState("");
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedHandleId, setSelectedHandleId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedBaseUnit, setSelectedBaseUnit] = useState("");
  const [selectedWallUnit, setSelectedWallUnit] = useState("");
  const [selectedTallUnit, setSelectedTallUnit] = useState("");
  const [selectedCountertop, setSelectedCountertop] = useState("");
  const [openColorSection, setOpenColorSection] = useState("baseUnit");
  const [sizeOpen, setSizeOpen] = useState(true);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    let alive = true;

    async function loadAllProducts() {
      setLoading(true);
      setError("");
      try {
        if (!window.VMKitchen?.getProducts) {
          throw new Error("Dynamic data helper is not available.");
        }
        const items = await window.VMKitchen.getProducts();
        const configs = await Promise.all(
          items.map((item) => window.VMKitchen.getProductConfig(item.id)),
        );
        if (!alive) return;
        setProductConfigs(configs);
        if (configs.length === 0) setError("No dynamic kitchen products found.");
      } catch (loadError) {
        if (!alive) return;
        setError(loadError.message || "Unable to load dynamic products.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAllProducts();
    return () => {
      alive = false;
    };
  }, []);

  const sizeValue = parseFloat(kitchenSize);
  const hasSize = !!kitchenSize && sizeValue > 0;
  const configSize = (item) =>
    Number(item.measurements?.total_size || parseFloat(item.properties?.kitchen_size) || 0);
  // Show all products whose size is <= entered size (e.g. 6 → 6,5,4,3,2,1).
  const eligibleConfigs = hasSize
    ? productConfigs.filter((item) => {
        const itemSize = configSize(item);
        return itemSize > 0 && itemSize <= sizeValue;
      })
    : [];
  const allShapes = Array.from(
    new Map(
      productConfigs
        .flatMap((item) => item.shapes ?? [])
        .filter(Boolean)
        .map((term) => [String(term.id), term]),
    ).values(),
  );
  const availableShapes = Array.from(
    new Map(
      eligibleConfigs
        .map((item) => termById(item.shapes, item.properties?.shape_id))
        .filter(Boolean)
        .map((term) => [String(term.id), term]),
    ).values(),
  );
  const availableShapeIds = new Set(availableShapes.map((term) => Number(term.id)));
  const shapeConfigs = selectedShapeId
    ? eligibleConfigs.filter((item) => Number(item.properties?.shape_id) === Number(selectedShapeId))
    : [];
  const availableHandles = Array.from(
    new Map(
      shapeConfigs
        .map((item) => termById(item.handles, item.properties?.handle_id))
        .filter(Boolean)
        .map((term) => [String(term.id), term]),
    ).values(),
  );
  const allHandles = Array.from(
    new Map(
      productConfigs
        .flatMap((item) => item.handles ?? [])
        .filter(Boolean)
        .map((term) => [String(term.id), term]),
    ).values(),
  );
  const availableHandleIds = new Set(availableHandles.map((term) => Number(term.id)));
  // The static app orders the design grid by size ascending; WooCommerce hands
  // products back in post order, so sort here rather than rely on the query.
  const designConfigs = selectedHandleId
    ? shapeConfigs
        .filter((item) => Number(item.properties?.handle_id) === Number(selectedHandleId))
        .sort(
          (a, b) =>
            configSize(a) - configSize(b) ||
            String(a.product?.article_number ?? "").localeCompare(
              String(b.product?.article_number ?? ""),
            ),
        )
    : [];
  const config =
    designConfigs.find((item) => Number(item.product?.id) === Number(selectedProductId)) ?? null;
  const shape = termById(config?.shapes, config?.properties?.shape_id);
  const handle = termById(config?.handles, config?.properties?.handle_id);
  const design = termById(config?.designs, config?.properties?.design_id);
  const baseTextures = colorsFor(config, "base_unit");
  const wallTextures = colorsFor(config, "wall_unit");
  const tallTextures = colorsFor(config, "tall_unit");
  const countertopTexturesDynamic = colorsFor(config, "countertop");
  const measurements = config?.measurements ?? {};
  // A unit exists for this article when it has been priced, exactly as the static
  // app reads woodUnits.group1.wall > 0. The measurement lengths are not captured
  // in the admin form, so they can never answer this.
  const hasWallUnit = dynamicMatrixPrice(config, "wall_unit", "g1") > 0;
  const hasTallUnit = dynamicMatrixPrice(config, "tall_unit", "g1") > 0;
  const currentHandle = handleKind(handle);

  useEffect(() => {
    const shapeStillAvailable = availableShapes.some(
      (term) => Number(term.id) === Number(selectedShapeId),
    );
    if (selectedShapeId && !shapeStillAvailable) {
      setSelectedShapeId(null);
      setSelectedHandleId(null);
      setSelectedProductId(null);
    }
  }, [kitchenSize, productConfigs.length]);

  useEffect(() => {
    if (!selectedShapeId) return;
    const handleStillAvailable = availableHandles.some(
      (term) => Number(term.id) === Number(selectedHandleId),
    );
    if (selectedHandleId && !handleStillAvailable) {
      setSelectedHandleId(null);
      setSelectedProductId(null);
    }
  }, [selectedShapeId, kitchenSize]);

  useEffect(() => {
    if (!selectedHandleId) return;
    const productStillAvailable = designConfigs.some(
      (item) => Number(item.product?.id) === Number(selectedProductId),
    );
    if (selectedProductId && !productStillAvailable) {
      setSelectedProductId(null);
    }
  }, [selectedHandleId, kitchenSize]);

  useEffect(() => {
    if (!config) {
      onModelSelect?.(null);
      onInfoChange?.(null);
      onPriceChange?.(0);
      onCartConfigChange?.(null);
      return;
    }

    const base = preferredTexture(baseTextures, DEFAULT_WOOD_COLOR, selectedBaseUnit);
    const wall =
      preferredTexture(wallTextures, DEFAULT_WOOD_COLOR, selectedWallUnit) ?? base;
    const tall =
      preferredTexture(tallTextures, DEFAULT_WOOD_COLOR, selectedTallUnit) ?? base;
    const countertop = preferredTexture(
      countertopTexturesDynamic,
      DEFAULT_SURFACE_COLOR,
      selectedCountertop,
    );

    setSelectedBaseUnit(base?.id ?? "");
    setSelectedWallUnit(wall?.id ?? "");
    setSelectedTallUnit(tall?.id ?? "");
    setSelectedCountertop(countertop?.id ?? "");
    onTextureChange?.("baseUnit", base?.renderValue ?? null);
    onTextureChange?.("wallUnit", wall?.renderValue ?? base?.renderValue ?? null);
    onTextureChange?.("tallUnit", tall?.renderValue ?? base?.renderValue ?? null);
    onTextureChange?.("countertop", countertop?.renderValue ?? null);
    onTextureChange?.("handle", currentHandle === "with" ? "show" : "hidden");
    onModelSelect?.(dynamicModelPath(config, design, currentHandle));
    onInfoChange?.({
      dynamic: true,
      productConfig: config,
      shapeId: shape?.slug ?? shape?.id,
      handle: currentHandle,
      design: {
        id: design?.slug || config.product?.article_number || String(config.product?.id ?? ""),
        size: Number(measurements.total_size || 0),
        name: design?.name || config.product?.name,
      },
    });
  }, [config?.product?.id]);

  const cartConfig = config
    ? {
        product_id: Number(config.product.id),
        shape_id: Number(config.properties?.shape_id || 0),
        handle_id: Number(config.properties?.handle_id || 0),
        design_id: Number(config.properties?.design_id || 0),
        base_color_id: selectedColorId(selectedBaseUnit),
        wall_color_id: selectedColorId(selectedWallUnit),
        tall_color_id: selectedColorId(selectedTallUnit),
        countertop_color_id: selectedColorId(selectedCountertop),
        quantity: 1,
      }
    : null;

  useEffect(() => {
    const hasRequiredColors =
      cartConfig &&
      cartConfig.base_color_id > 0 &&
      (!hasWallUnit || cartConfig.wall_color_id > 0) &&
      (!hasTallUnit || cartConfig.tall_color_id > 0) &&
      cartConfig.countertop_color_id > 0;
    onCartConfigChange?.(hasRequiredColors ? cartConfig : null);
  }, [
    config?.product?.id,
    selectedBaseUnit,
    selectedWallUnit,
    selectedTallUnit,
    selectedCountertop,
    hasWallUnit,
    hasTallUnit,
  ]);

  useEffect(() => {
    onPriceChange?.(calculateDynamicPrice(config, cartConfig));
  }, [
    config?.product?.id,
    selectedBaseUnit,
    selectedWallUnit,
    selectedTallUnit,
    selectedCountertop,
  ]);

  function handleSizeChange(value) {
    setKitchenSize(value);
  }

  /*
   * The design the grid shows first.
   *
   * Must use the same comparator as designConfigs - size, then article number -
   * or the auto-selected design is not the one highlighted at the top left.
   * Several designs share a size (two at 6LM here), and sorting on size alone
   * left the tie to array order, which picked the second card.
   */
  function smallestConfig(items) {
    return (
      items
        .slice()
        .sort(
          (a, b) =>
            configSize(a) - configSize(b) ||
            String(a.product?.article_number ?? "").localeCompare(
              String(b.product?.article_number ?? ""),
            ),
        )[0] ?? null
    );
  }

  function handlesForConfigs(items) {
    return Array.from(
      new Map(
        items
          .map((item) => termById(item.handles, item.properties?.handle_id))
          .filter(Boolean)
          .map((term) => [String(term.id), term]),
      ).values(),
    );
  }

  /*
   * Picking a shape opens on "no handle" with the smallest design already
   * loaded, as the original app does. It used to clear the handle and the
   * design, leaving an empty viewport and a zero price until the customer made
   * two more choices.
   */
  function handleShapeSelect(shapeId) {
    setSelectedShapeId(shapeId);

    const forShape = eligibleConfigs.filter(
      (item) => Number(item.properties?.shape_id) === Number(shapeId),
    );
    const handles = handlesForConfigs(forShape);

    // Changing shape keeps the handle already chosen when this shape offers it.
    const handleTerm =
      handles.find((term) => Number(term.id) === Number(selectedHandleId)) ??
      handles.find((term) => handleKind(term) === "none") ??
      handles[0] ??
      null;

    setSelectedHandleId(handleTerm ? handleTerm.id : null);

    const opening = smallestConfig(
      handleTerm
        ? forShape.filter(
            (item) => Number(item.properties?.handle_id) === Number(handleTerm.id),
          )
        : [],
    );

    setSelectedProductId(opening ? opening.product.id : null);
  }

  /*
   * Toggling the handle shows the SAME kitchen with or without handles. The two
   * variants are separate products here, so the counterpart is found by SKU
   * stem (see designStem) rather than kept in one record with two model files
   * the way the original app does it.
   */
  function handleHandleSelect(handleId) {
    setSelectedHandleId(handleId);

    const forHandle = shapeConfigs.filter(
      (item) => Number(item.properties?.handle_id) === Number(handleId),
    );
    const current = shapeConfigs.find(
      (item) => Number(item.product?.id) === Number(selectedProductId),
    );
    const stem = current ? designStem(current) : "";
    const twin = stem ? forHandle.find((item) => designStem(item) === stem) : null;
    const next = twin ?? smallestConfig(forHandle);

    setSelectedProductId(next ? next.product.id : null);
  }

  function handleDesignSelect(item) {
    setSelectedProductId(item.product.id);
  }

  function selectTexture(component, setter, texture) {
    setter(texture.id);
    onTextureChange?.(component, texture.renderValue ?? texture.path);
  }

  if (loading) {
    return <div className="bg-white h-full" />;
  }

  if (error) {
    return (
      <div className="bg-white h-full grid place-items-center p-8 text-sm text-red-500 text-center">
        {error}
      </div>
    );
  }

  if (productConfigs.length === 0) {
    return (
      <div className="bg-white h-full grid place-items-center p-8 text-sm text-gray-500 text-center">
        No dynamic kitchen products found. Enable Kitchen Configurator on a published simple product.
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* The panel heading, matching the wardrobe's. It had been commented out,
          which is why only the wardrobe showed one. */}
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-center text-sm font-bold tracking-[0.2em] uppercase text-gray-900">{t("Build Your Kitchen")}</h1>
      </div>

      <div className="border-t border-gray-200">
        <AccordionSection
          title={t("Kitchen Size")}
          isOpen={sizeOpen}
          onToggle={() => setSizeOpen((v) => !v)}
        >
          <div className="px-5 pt-4 pb-5 space-y-3">
            <div className="flex items-center border border-gray-300">
              <label className="size-class flex items-center px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap border-r border-gray-300 bg-gray-50 flex items-center gap-1.5">{t("Size in LM")}<InfoTooltip text={t("Kitchen designs will be displayed for all sizes up to the entered kitchen size.")} />
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={kitchenSize}
                onChange={(event) => handleSizeChange(event.target.value)}
                className="flex-1 min-w-0 px-4 py-2.5 text-sm text-right outline-none placeholder:text-[10px] md:placeholder:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none appearance-none"
                placeholder={t("Enter your kitchen size in LM")}
              />
              <div className="flex border-l border-gray-300">
                <button
                  onClick={() =>
                    handleSizeChange(String((parseFloat(kitchenSize) || 0) + 1))
                  }
                  className="px-3 py-2.5 text-gray-500 hover:bg-gray-100 border-r border-gray-300 text-[10px] transition-colors"
                >
                  ▲
                </button>
                <button
                  onClick={() =>
                    handleSizeChange(
                      String(Math.max(1, (parseFloat(kitchenSize) || 0) - 1)),
                    )
                  }
                  className="px-3 py-2.5 text-gray-500 hover:bg-gray-100 text-[10px] transition-colors"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>
        </AccordionSection>

        {hasSize && (
          <AccordionSection title={t("Kitchen Options")} defaultOpen={true}>
            <div className="px-5 pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-3">{t("Kitchen Shape")}</p>
              {allShapes.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">{t("No kitchen shapes available.")}</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {allShapes.map((shapeTerm) => {
                    const available = availableShapeIds.has(Number(shapeTerm.id));
                    const fallbackShape =
                      SHAPES.find((staticShape) => staticShape.id === shapeTerm.slug) ?? {};
                    const shapeCard = {
                      id: shapeTerm.slug || String(shapeTerm.id),
                      label: shapeTerm.label || shapeTerm.name || fallbackShape.label || "",
                      thumbnail: shapeTerm.thumbnail || fallbackShape.thumbnail || "",
                      thumbnailHover:
                        shapeTerm.thumbnailHover ||
                        shapeTerm.thumbnail ||
                        fallbackShape.thumbnailHover ||
                        "",
                    };
                    const card = (
                      <ShapeCard
                        shape={shapeCard}
                        available={available}
                        selected={available && Number(selectedShapeId) === Number(shapeTerm.id)}
                        onClick={() => handleShapeSelect(shapeTerm.id)}
                      />
                    );
                    return (
                      <div key={shapeTerm.id} className="flex flex-col items-center gap-1.5">
                        {!available ? (
                          <DisabledTooltip className="w-full">
                            {card}
                          </DisabledTooltip>
                        ) : (
                          <div className="w-full">{card}</div>
                        )}
                        <span
                          className={`text-xs text-center ${
                            available ? "text-gray-600" : "text-gray-300"
                          }`}
                        >
                          {shapeCard.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedShapeId && (
              <div className="px-5 pb-2">
                <p className="text-xs mb-3 text-gray-500">{t("Handles")}</p>
                <div className="flex flex-wrap gap-3">
                  {allHandles.map((handleTerm) => {
                    const available = availableHandleIds.has(Number(handleTerm.id));
                    const button = (
                      <button
                        key={handleTerm.id}
                        onClick={() => available && handleHandleSelect(handleTerm.id)}
                        disabled={!available}
                        className={`w-full py-2.5 border text-xs font-bold tracking-wider uppercase ${
                          !available
                            ? "border-gray-200 text-gray-300 bg-white cursor-default"
                            : Number(selectedHandleId) === Number(handleTerm.id)
                              ? "border-gray-900 bg-gray-100"
                              : "border-gray-300 bg-white"
                        }`}
                      >
                        {handleTerm.label || handleTerm.name}
                      </button>
                    );
                    return !available ? (
                      <DisabledTooltip key={handleTerm.id} className="flex-1">
                        {button}
                      </DisabledTooltip>
                    ) : (
                      <div key={handleTerm.id} className="flex-1">
                        {button}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedShapeId && selectedHandleId && (
              <>
                <div className="px-5 h-7 flex items-center justify-center">
                  {designConfigs.length === 0 && (
                    <p className="text-xs text-gray-400 text-center">{t("No kitchens available for this size, shape, and handle.")}</p>
                  )}
                </div>
                {designConfigs.length > 0 && (
                  <>
                    <div className="px-5 mb-3">
                      <p className="text-xs text-gray-500">{t("Kitchen Design")}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3 px-5 pb-5">
                      {designConfigs
                        .slice()
                        .sort((a, b) => configSize(a) - configSize(b))
                        .map((item) => (
                          <button
                            key={item.product.id}
                            onClick={() => handleDesignSelect(item)}
                            className="flex flex-col items-center gap-1.5 w-full"
                          >
                            <div
                              className={`w-full aspect-square overflow-hidden border transition-colors ${
                                Number(selectedProductId) === Number(item.product.id)
                                  ? "bg-gray-100 border-gray-900"
                                  : "bg-gray-50 border-transparent hover:border-gray-300"
                              }`}
                            >
                              <img
                                src={dynamicThumbnailPath(
                                  item,
                                  termById(item.designs, item.properties?.design_id),
                                )}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(event) => {
                                  event.currentTarget.hidden = true;
                                }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 text-center leading-tight">
                              {configSize(item) || item.properties?.kitchen_size}LM
                            </span>
                          </button>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}
          </AccordionSection>
        )}

        {config && (
          <>
            <AccordionSection
              title={t("Base Unit Color")}
              isOpen={openColorSection === "baseUnit"}
              onToggle={() =>
                setOpenColorSection(openColorSection === "baseUnit" ? null : "baseUnit")
              }
            >
              <TextureGrid
                textures={baseTextures}
                selectedId={selectedBaseUnit}
                onSelect={(tex) => selectTexture("baseUnit", setSelectedBaseUnit, tex)}
              />
            </AccordionSection>

            {hasWallUnit && (
              <AccordionSection
                title={t("Wall Unit Color")}
                isOpen={openColorSection === "wallUnit"}
                onToggle={() =>
                  setOpenColorSection(openColorSection === "wallUnit" ? null : "wallUnit")
                }
              >
                <TextureGrid
                  textures={wallTextures}
                  selectedId={selectedWallUnit}
                  onSelect={(tex) => selectTexture("wallUnit", setSelectedWallUnit, tex)}
                />
              </AccordionSection>
            )}

            {hasTallUnit && (
              <AccordionSection
                title={t("Tall Unit Color")}
                isOpen={openColorSection === "tallUnit"}
                onToggle={() =>
                  setOpenColorSection(openColorSection === "tallUnit" ? null : "tallUnit")
                }
              >
                <TextureGrid
                  textures={tallTextures}
                  selectedId={selectedTallUnit}
                  onSelect={(tex) => selectTexture("tallUnit", setSelectedTallUnit, tex)}
                />
              </AccordionSection>
            )}

            <AccordionSection
              title={t("Countertop Color")}
              isOpen={openColorSection === "countertop"}
              onToggle={() =>
                setOpenColorSection(openColorSection === "countertop" ? null : "countertop")
              }
            >
              <TextureGrid
                textures={countertopTexturesDynamic}
                selectedId={selectedCountertop}
                onSelect={(tex) => selectTexture("countertop", setSelectedCountertop, tex)}
              />
            </AccordionSection>
          </>
        )}
      </div>
    </div>
  );
}

function StaticConfigurator({
  onModelSelect,
  onTextureChange,
  onPriceChange,
  onInfoChange,
}) {
  const [kitchenSize, setKitchenSize] = useState("");
  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedHandle, setSelectedHandle] = useState(null); // 'none' | 'with'
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [selectedBaseUnit, setSelectedBaseUnit] = useState(
    cabinetTextures[0]?.id ?? "",
  );
  const [selectedWallUnit, setSelectedWallUnit] = useState(
    cabinetTextures[0]?.id ?? "",
  );
  const [selectedTallUnit, setSelectedTallUnit] = useState(
    cabinetTextures[0]?.id ?? "",
  );
  const [selectedCountertop, setSelectedCountertop] = useState(
    countertopTextures[0]?.id ?? "",
  );
  const [openColorSection, setOpenColorSection] = useState("baseUnit");
  const [sizeOpen, setSizeOpen] = useState(true);

  const sizeValue = parseFloat(kitchenSize);
  const hasSize = !!kitchenSize && sizeValue > 0;
  const hasShapeAndHandle = selectedShape !== null && selectedHandle !== null;
  const hasDesign = selectedDesignId !== null;

  const shapeDesigns =
    kitchenLayouts.find((l) => l.id === selectedShape)?.designs ?? [];
  const filteredDesigns = shapeDesigns
    .filter((d) => d.size <= sizeValue)
    .sort((a, b) => a.size - b.size);

  const shapeHasDesigns = (shapeId) =>
    kitchenLayouts
      .find((l) => l.id === shapeId)
      ?.designs.some((d) => d.size <= sizeValue) ?? false;

  const hasAnyDesignForSize =
    hasSize && SHAPES.some((s) => shapeHasDesigns(s.id));

  // Which unit types the selected design actually has
  const selectedPricing = selectedDesignId
    ? designPricing[selectedDesignId]
    : null;
  const hasWallUnit = selectedPricing
    ? selectedPricing.woodUnits["group1"].wall > 0
    : false;
  const hasTallUnit = selectedPricing
    ? selectedPricing.woodUnits["group1"].tall > 0
    : false;

  // Pricing
  const totalPrice = (() => {
    if (!hasDesign) return 0;
    const p = designPricing[selectedDesignId];
    if (!p) return 0;
    const ctTier = countertopTiers[selectedCountertop] ?? "group1";
    const baseGrp = cabinetTextureGroups[selectedBaseUnit] ?? "group1";
    const wallGrp = cabinetTextureGroups[selectedWallUnit] ?? "group1";
    const tallGrp = cabinetTextureGroups[selectedTallUnit] ?? "group1";
    return (
      p.countertop[ctTier] +
      p.woodUnits[baseGrp].base +
      p.woodUnits[wallGrp].wall +
      p.woodUnits[tallGrp].tall +
      p.skirting +
      p.handle[selectedHandle === "none" ? "gola" : "with"]
    );
  })();

  useEffect(() => {
    onPriceChange?.(totalPrice);
  }, [totalPrice]);

  useEffect(() => {
    if (!hasWallUnit && openColorSection === "wallUnit")
      setOpenColorSection("baseUnit");
    if (!hasTallUnit && openColorSection === "tallUnit")
      setOpenColorSection("baseUnit");
  }, [selectedDesignId]);

  function handleSizeChange(value) {
    setKitchenSize(value);
    const newSize = parseFloat(value);

    // Nothing selected yet — just update size
    if (!selectedShape || !selectedDesignId) return;

    const allDesigns =
      kitchenLayouts.find((l) => l.id === selectedShape)?.designs ?? [];
    const currentDesign = allDesigns.find((d) => d.id === selectedDesignId);

    // Current design still fits — keep everything as-is
    if (currentDesign && currentDesign.size <= newSize) return;

    // Current design no longer fits — find largest design that still fits
    const available = allDesigns
      .filter((d) => d.size <= newSize)
      .sort((a, b) => a.size - b.size);

    if (available.length === 0) {
      // No designs available at this size — hide model but keep shape & handle selected
      setSelectedDesignId(null);
      onModelSelect?.(null);
      onInfoChange?.(null);
      return;
    }

    const best = available[available.length - 1];
    setSelectedDesignId(best.id);
    const handle = selectedHandle ?? "none";
    onModelSelect?.(handle === "none" ? best.modelGola : best.model);
    onInfoChange?.({ shapeId: selectedShape, handle, design: best });
  }

  function handleShapeSelect(shapeId) {
    setSizeOpen(false);
    setSelectedShape(shapeId);

    // Find smallest available design for this shape
    const designs = kitchenLayouts.find((l) => l.id === shapeId)?.designs ?? [];
    const available = designs
      .filter((d) => d.size <= sizeValue)
      .sort((a, b) => a.size - b.size);
    const smallest = available[0];

    if (!smallest) {
      setSelectedDesignId(null);
      onModelSelect?.(null);
      onInfoChange?.(null);
      return;
    }

    // Default to 'none' on first shape pick; keep existing handle when changing shape
    const handle = selectedHandle ?? "none";
    if (!selectedHandle) {
      setSelectedHandle("none");
      onTextureChange?.("handle", "hidden");
    }

    setSelectedDesignId(smallest.id);
    onModelSelect?.(handle === "none" ? smallest.modelGola : smallest.model);
    onInfoChange?.({ shapeId, handle, design: smallest });
  }

  function handleHandleSelect(type) {
    setSizeOpen(false);
    setSelectedHandle(type);
    onTextureChange?.("handle", type === "with" ? "show" : "hidden");
    if (selectedDesignId) {
      const design = shapeDesigns.find((d) => d.id === selectedDesignId);
      if (design) {
        onModelSelect?.(type === "none" ? design.modelGola : design.model);
        onInfoChange?.({ shapeId: selectedShape, handle: type, design });
      }
    }
  }

  function handleDesignSelect(design) {
    setSelectedDesignId(design.id);
    onModelSelect?.(
      selectedHandle === "none" ? design.modelGola : design.model,
    );
    onInfoChange?.({ shapeId: selectedShape, handle: selectedHandle, design });
  }

  return (
    <div className="bg-white">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-center text-sm font-bold tracking-[0.2em] uppercase text-gray-900">{t("Build Your Kitchen")}</h1>
      </div>

      <div className="border-t border-gray-200">
        {/* Kitchen Size */}
        <AccordionSection
          title={t("Kitchen Size")}
          isOpen={sizeOpen}
          onToggle={() => setSizeOpen((v) => !v)}
        >
          <div className="px-5 pt-4 pb-5 space-y-4">
            <div className="flex items-center border border-gray-300">
              <label className="flex items-center px-4 py-2.5 flex items-center justify-between text-xs text-gray-500 whitespace-nowrap border-r border-gray-300 bg-gray-50 flex items-center gap-1.5">{t("Size in LM")}<InfoTooltip text={t("Kitchen designs will be displayed based on the nearest available kitchen size.")} />
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={kitchenSize}
                onChange={(e) => handleSizeChange(e.target.value)}
                className="flex-1 min-w-0 px-4 py-2.5 text-sm text-right outline-none placeholder:text-[10px] md:placeholder:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none appearance-none"
                placeholder={t("Enter your kitchen size in LM")}
              />
              <div className="flex border-l border-gray-300">
                <button
                  onClick={() =>
                    handleSizeChange(String((parseFloat(kitchenSize) || 0) + 1))
                  }
                  className="px-3 py-2.5 text-gray-500 hover:bg-gray-100 border-r border-gray-300 text-[10px] transition-colors"
                >
                  ▲
                </button>
                <button
                  onClick={() =>
                    handleSizeChange(
                      String(Math.max(1, (parseFloat(kitchenSize) || 0) - 1)),
                    )
                  }
                  className="px-3 py-2.5 text-gray-500 hover:bg-gray-100 text-[10px] transition-colors"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>
        </AccordionSection>

        {hasSize && (
          <AccordionSection title={t("Kitchen Options")} defaultOpen={true}>
            {/* Kitchen Shape */}
            <div className="px-5 pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-3">{t("Kitchen Shape")}</p>
              <div className="grid grid-cols-4 gap-2">
                {SHAPES.map((shape) => {
                  const available = shapeHasDesigns(shape.id);
                  const card = (
                    <ShapeCard
                      shape={shape}
                      available={available}
                      selected={selectedShape === shape.id}
                      onClick={() => handleShapeSelect(shape.id)}
                    />
                  );
                  return (
                    <div
                      key={shape.id}
                      className="flex flex-col items-center gap-1.5"
                    >
                      {!available ? (
                        <DisabledTooltip className="w-full">
                          {card}
                        </DisabledTooltip>
                      ) : (
                        <div className="w-full">{card}</div>
                      )}
                      <span
                        className={`text-xs ${available ? "text-gray-600" : "text-gray-300"}`}
                      >
                        {shape.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Handles */}
            <div className="px-5 pb-2">
              <p
                className={`text-xs mb-3 ${hasAnyDesignForSize ? "text-gray-500" : "text-gray-300"}`}
              >{t("Handles")}</p>
              <div className="flex gap-3">
                {[
                  { type: "none", label: t("No Handle") },
                  { type: "with", label: t("With Handle") },
                ].map(({ type, label }) => {
                  const btn = (
                    <button
                      key={type}
                      onClick={() =>
                        hasAnyDesignForSize && handleHandleSelect(type)
                      }
                      disabled={!hasAnyDesignForSize}
                      className={`w-full py-2.5 border text-xs font-bold tracking-wider uppercase ${
                        !hasAnyDesignForSize
                          ? "border-gray-200 text-gray-300 bg-white cursor-default"
                          : selectedHandle === type
                            ? "border-gray-900 bg-gray-100"
                            : "border-gray-300 bg-white"
                      }`}
                    >
                      {label}
                    </button>
                  );
                  return !hasAnyDesignForSize ? (
                    <DisabledTooltip key={type} className="flex-1">
                      {btn}
                    </DisabledTooltip>
                  ) : (
                    <div key={type} className="flex-1">
                      {btn}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* No designs available — fixed height so layout doesn't shift */}
            <div className="px-5 h-7 flex items-center justify-center">
              {!hasAnyDesignForSize && (
                <p className="text-xs text-gray-400 text-center">{t("No designs available for this size and shape.")}</p>
              )}
            </div>

            {/* Design grid — appears after shape + handle selected */}
            {hasAnyDesignForSize && hasShapeAndHandle && (
              <>
                <div className="px-5 mb-3">
                  <p className="text-xs text-gray-500">{t("Kitchen Design")}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 px-5 pb-5">
                  {filteredDesigns.map((design) => (
                    <DesignCard
                      key={design.id}
                      design={design}
                      selected={selectedDesignId === design.id}
                      onClick={() => handleDesignSelect(design)}
                      handleType={selectedHandle}
                    />
                  ))}
                </div>
              </>
            )}
          </AccordionSection>
        )}

        {/* Color sections — appear after design is selected, only one open at a time */}
        {hasDesign && (
          <>
            <AccordionSection
              title={t("Base Unit Color")}
              isOpen={openColorSection === "baseUnit"}
              onToggle={() =>
                setOpenColorSection(
                  openColorSection === "baseUnit" ? null : "baseUnit",
                )
              }
            >
              <TextureGrid
                textures={cabinetTextures}
                selectedId={selectedBaseUnit}
                onSelect={(tex) => {
                  setSelectedBaseUnit(tex.id);
                  onTextureChange?.("baseUnit", tex.path ?? null);
                }}
              />
            </AccordionSection>

            {hasWallUnit && (
              <AccordionSection
                title={t("Wall Unit Color")}
                isOpen={openColorSection === "wallUnit"}
                onToggle={() =>
                  setOpenColorSection(
                    openColorSection === "wallUnit" ? null : "wallUnit",
                  )
                }
              >
                <TextureGrid
                  textures={cabinetTextures}
                  selectedId={selectedWallUnit}
                  onSelect={(tex) => {
                    setSelectedWallUnit(tex.id);
                    onTextureChange?.("wallUnit", tex.path ?? null);
                  }}
                />
              </AccordionSection>
            )}

            {hasTallUnit && (
              <AccordionSection
                title={t("Tall Unit Color")}
                isOpen={openColorSection === "tallUnit"}
                onToggle={() =>
                  setOpenColorSection(
                    openColorSection === "tallUnit" ? null : "tallUnit",
                  )
                }
              >
                <TextureGrid
                  textures={cabinetTextures}
                  selectedId={selectedTallUnit}
                  onSelect={(tex) => {
                    setSelectedTallUnit(tex.id);
                    onTextureChange?.("tallUnit", tex.path ?? null);
                  }}
                />
              </AccordionSection>
            )}

            <AccordionSection
              title={t("Countertop Color")}
              isOpen={openColorSection === "countertop"}
              onToggle={() =>
                setOpenColorSection(
                  openColorSection === "countertop" ? null : "countertop",
                )
              }
            >
              <TextureGrid
                textures={countertopTextures}
                selectedId={selectedCountertop}
                onSelect={(tex) => {
                  setSelectedCountertop(tex.id);
                  onTextureChange?.("countertop", tex.path ?? null);
                }}
              />
            </AccordionSection>
          </>
        )}
      </div>
    </div>
  );
}
